import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { MODULES } from '@yourstore/domain';
import { useAction, useApi } from './context';
import { Failure, Section, When } from './bits';
import type { ModuleRow, ModuleStatus } from '../wire';

/**
 * What this business has bought (spec §3.1).
 *
 * Three properties of the server are honoured here rather than re-implemented:
 *
 *  1. **One request, not one per tick.** Dependencies are validated against the
 *     state the request would *leave behind*, so `clients + services + calendar
 *     + partner_api` succeeds as one call and would be refused as four. The
 *     screen therefore collects a whole intention and sends it once.
 *  2. **The dependency refusal is the server's to make.** This console does not
 *     pre-compute the DAG and grey things out: it shows what depends on what,
 *     lets the operator ask, and renders the `409` — "products, certificates
 *     still depend on sales", with the hint naming the fix. A UI that silently
 *     prevented the ask would be a second, quieter copy of a rule that already
 *     has an authoritative home, and the two would drift.
 *  3. **Disabling hides; it never deletes.** The server says so in its `note`
 *     and the section repeats it, because it is the property people are
 *     surprised by — the rows stay, the routes stop answering, re-enabling
 *     restores it.
 *
 * A module the tenant has no row for yet reads as `off`, which is what it
 * effectively is; the catalogue itself comes from `vocabulary.ts`, since a
 * freshly created tenant has no rows for the server to label.
 */
export function TenantModules({
  tenantId,
  modules,
  onChanged,
}: {
  tenantId: string;
  modules: ModuleRow[];
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const action = useAction();
  const [want, setWant] = useState<Record<string, ModuleStatus>>({});
  const [note, setNote] = useState<string | null>(null);

  const current = useMemo(() => {
    const map = new Map<string, ModuleRow>();
    for (const m of modules) map.set(m.module, m);
    return map;
  }, [modules]);

  const statusOf = (key: string): ModuleStatus =>
    want[key] ?? current.get(key)?.status ?? ('off' as const);

  const change = useMemo(() => {
    const enable: string[] = [];
    const trial: string[] = [];
    const disable: string[] = [];
    for (const [key, desired] of Object.entries(want)) {
      const now = current.get(key)?.status ?? 'off';
      if (desired === now) continue;
      if (desired === 'on') enable.push(key);
      else if (desired === 'trial') trial.push(key);
      else disable.push(key);
    }
    return { enable, trial, disable };
  }, [want, current]);

  const dirty = change.enable.length + change.trial.length + change.disable.length > 0;

  const apply = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.setModules(tenantId, change);
      setNote(`${answer.enabled.join(', ') || 'nothing'} enabled. ${answer.note}`);
    });
    if (ok) {
      setWant({});
      onChanged();
    }
  };

  return (
    <Section
      title="Modules"
      note="Authorization is key.scopes ∩ scopes(tenant.modules) — a scope whose module is off can never be granted."
      actions={
        <div className="row-actions">
          {dirty ? (
            <button
              type="button"
              className="button"
              onClick={() => {
                action.clear();
                setWant({});
              }}
              disabled={action.busy}
            >
              Reset
            </button>
          ) : null}
          <button
            type="button"
            className="button button--primary"
            onClick={() => void apply()}
            disabled={!dirty || action.busy}
          >
            {action.busy ? 'Applying…' : 'Apply changes'}
          </button>
        </div>
      }
    >
      {action.failure !== null ? <Failure failure={action.failure} /> : null}
      {note !== null ? <p className="ok">{note}</p> : null}

      <table className="table table--dense">
        <thead>
          <tr>
            <th>Module</th>
            <th>Depends on</th>
            <th>Since</th>
            <th>State</th>
          </tr>
        </thead>
        <tbody>
          {MODULES.map((spec) => {
            const row = current.get(spec.key);
            const value = statusOf(spec.key);
            const changed = value !== (row?.status ?? 'off');
            return (
              <tr key={spec.key} className={changed ? 'is-dirty' : undefined}>
                <td>
                  <code>{spec.key}</code>
                  <span className="muted"> {spec.label}</span>
                  {spec.kind === 'platform' ? (
                    <span className="flag">platform — never sold</span>
                  ) : null}
                </td>
                <td className="muted">
                  {spec.dependsOn.length === 0 ? '—' : spec.dependsOn.join(', ')}
                </td>
                <td>
                  <When iso={row?.enabled_at ?? null} />
                </td>
                <td>
                  <select
                    aria-label={`${spec.key} state`}
                    value={value}
                    onChange={(e) =>
                      setWant((w) => ({ ...w, [spec.key]: e.target.value as ModuleStatus }))
                    }
                  >
                    <option value="off">off</option>
                    <option value="on">on</option>
                    <option value="trial">trial</option>
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Section>
  );
}
